/**
 * AudioDumper - captures raw opus frames from websocket messages and
 * downloads them as a playable OGG/Opus file.
 *
 * Usage:
 *   audioDumper.start()    - begin recording
 *   audioDumper.stop()     - stop recording
 *   audioDumper.download() - download as .ogg file
 *   audioDumper.clear()    - discard captured data
 */

// ── OGG page builder ────────────────────────────────────────────────

const OGG_CAPTURE_PATTERN = new Uint8Array([0x4f, 0x67, 0x67, 0x53]); // "OggS"

function crc32Ogg(data: Uint8Array): number {
  // OGG uses a custom CRC-32 polynomial
  const table: number[] = [];
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let j = 0; j < 8; j++) {
      r = (r << 1) ^ ((r & 0x80000000 ? 0x04c11db7 : 0) >>> 0);
      r = r >>> 0;
    }
    table[i] = r;
  }

  let crc = 0;
  for (let i = 0; i < data.length; i++) {
    crc = ((crc << 8) ^ table[((crc >>> 24) ^ data[i]) & 0xff]) >>> 0;
  }
  return crc;
}

function buildOggPage(
  serialNo: number,
  pageSeqNo: number,
  granulePos: bigint,
  headerType: number,
  segments: Uint8Array[],
): Uint8Array {
  // Build segment table
  const segmentTable: number[] = [];
  for (const seg of segments) {
    let remaining = seg.length;
    while (remaining >= 255) {
      segmentTable.push(255);
      remaining -= 255;
    }
    segmentTable.push(remaining);
  }

  const totalDataSize = segments.reduce((s, seg) => s + seg.length, 0);
  const headerSize = 27 + segmentTable.length;
  const page = new Uint8Array(headerSize + totalDataSize);
  const view = new DataView(page.buffer);

  // Header
  page.set(OGG_CAPTURE_PATTERN, 0); // capture pattern
  page[4] = 0; // version
  page[5] = headerType; // header type
  view.setBigInt64(6, granulePos, true); // granule position
  view.setUint32(14, serialNo, true); // serial number
  view.setUint32(18, pageSeqNo, true); // page sequence number
  view.setUint32(22, 0, true); // CRC (filled later)
  page[26] = segmentTable.length; // number of segments

  // Segment table
  for (let i = 0; i < segmentTable.length; i++) {
    page[27 + i] = segmentTable[i];
  }

  // Data
  let offset = headerSize;
  for (const seg of segments) {
    page.set(seg, offset);
    offset += seg.length;
  }

  // CRC
  const crc = crc32Ogg(page);
  view.setUint32(22, crc, true);

  return page;
}

function buildOpusIdHeader(
  channels: number,
  sampleRate: number,
): Uint8Array {
  const header = new Uint8Array(19);
  const view = new DataView(header.buffer);

  // "OpusHead"
  header.set(
    new Uint8Array([0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64]),
    0,
  );
  header[8] = 1; // version
  header[9] = channels;
  view.setUint16(10, 0, true); // pre-skip
  view.setUint32(12, sampleRate, true); // input sample rate
  view.setUint16(16, 0, true); // output gain
  header[18] = 0; // channel mapping family

  return header;
}

function buildOpusCommentHeader(): Uint8Array {
  const vendor = "MixhaloAudioDumper";
  const vendorBytes = new TextEncoder().encode(vendor);
  const header = new Uint8Array(8 + 4 + vendorBytes.length + 4);
  const view = new DataView(header.buffer);

  // "OpusTags"
  header.set(
    new Uint8Array([0x4f, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73]),
    0,
  );
  view.setUint32(8, vendorBytes.length, true);
  header.set(vendorBytes, 12);
  view.setUint32(12 + vendorBytes.length, 0, true); // 0 user comments

  return header;
}

// ── Opus frame extractor ────────────────────────────────────────────

/**
 * Extracts raw opus frames from a websocket ArrayBuffer message.
 * Wire format: [4-byte header_len][JSON header][4-byte frame_len][opus_frame]...
 */
function extractOpusFrames(data: ArrayBuffer): Uint8Array[] {
  const frames: Uint8Array[] = [];
  try {
    const view = new DataView(data);
    const headerLength = view.getUint32(0);
    let bytePos = 4 + headerLength; // skip JSON header

    while (bytePos + 4 <= data.byteLength) {
      const frameLen = new DataView(data, bytePos, 4).getUint32(0);
      const frameEnd = bytePos + 4 + frameLen;
      if (frameEnd > data.byteLength) break;
      frames.push(new Uint8Array(data.slice(bytePos + 4, frameEnd)));
      bytePos = frameEnd;
    }
  } catch {
    // Silently skip malformed messages
  }
  return frames;
}

// ── AudioDumper ─────────────────────────────────────────────────────

const SAMPLE_RATE = 48000;
const CHANNELS = 2;
// Standard Opus frame duration at 48kHz = 960 samples (20ms)
const SAMPLES_PER_FRAME = 960;
// Max opus frames per OGG page
const MAX_FRAMES_PER_PAGE = 48;

export class AudioDumper {
  private opusFrames: Uint8Array[] = [];
  private _isRecording: boolean = false;

  get isRecording(): boolean {
    return this._isRecording;
  }

  get chunkCount(): number {
    return this.opusFrames.length;
  }

  start(): void {
    this._isRecording = true;
    console.log("[AudioDumper] Recording started");
  }

  stop(): void {
    this._isRecording = false;
    console.log(
      `[AudioDumper] Recording stopped. ${this.opusFrames.length} opus frames`,
    );
  }

  /**
   * Capture a raw websocket message. Extracts opus frames from the wire format.
   */
  capture(data: ArrayBuffer): void {
    if (!this._isRecording) return;
    const frames = extractOpusFrames(data);
    for (const frame of frames) {
      this.opusFrames.push(frame);
    }
  }

  clear(): void {
    this.opusFrames = [];
    console.log("[AudioDumper] Cleared");
  }

  /**
   * Build and download an OGG/Opus file from the captured frames.
   */
  download(filename?: string): void {
    if (this.opusFrames.length === 0) {
      console.warn("[AudioDumper] No data to download");
      return;
    }

    const serialNo = (Math.random() * 0xffffffff) >>> 0;
    const pages: Uint8Array[] = [];
    let pageSeqNo = 0;

    // Page 0: OpusHead (BOS)
    const idHeader = buildOpusIdHeader(CHANNELS, SAMPLE_RATE);
    pages.push(
      buildOggPage(serialNo, pageSeqNo++, BigInt(0), 0x02, [idHeader]),
    );

    // Page 1: OpusTags
    const commentHeader = buildOpusCommentHeader();
    pages.push(
      buildOggPage(serialNo, pageSeqNo++, BigInt(0), 0x00, [commentHeader]),
    );

    // Audio pages: pack up to MAX_FRAMES_PER_PAGE frames per page
    let granulePos = BigInt(0);
    for (let i = 0; i < this.opusFrames.length; ) {
      const end = Math.min(i + MAX_FRAMES_PER_PAGE, this.opusFrames.length);
      const pageFrames = this.opusFrames.slice(i, end);
      granulePos += BigInt(pageFrames.length * SAMPLES_PER_FRAME);

      const isLastPage = end === this.opusFrames.length;
      const headerType = isLastPage ? 0x04 : 0x00; // EOS flag on last page

      pages.push(
        buildOggPage(serialNo, pageSeqNo++, granulePos, headerType, pageFrames),
      );
      i = end;
    }

    // Combine all pages
    const totalSize = pages.reduce((s, p) => s + p.length, 0);
    const ogg = new Uint8Array(totalSize);
    let offset = 0;
    for (const page of pages) {
      ogg.set(page, offset);
      offset += page.length;
    }

    const blob = new Blob([ogg], { type: "audio/ogg" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      filename ||
      `audio-dump-${new Date().toISOString().replace(/[:.]/g, "-")}.ogg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    const durationSec =
      (this.opusFrames.length * SAMPLES_PER_FRAME) / SAMPLE_RATE;
    console.log(
      `[AudioDumper] Downloaded ${this.opusFrames.length} frames, ${durationSec.toFixed(1)}s, ${(totalSize / 1024).toFixed(1)} KB`,
    );
  }
}

/**
 * Global singleton. Available as window.__audioDumper in debug.
 */
export const audioDumper = new AudioDumper();

if (typeof window !== "undefined") {
  (window as any).__audioDumper = audioDumper;
}
